import type { ElasticScheme, LegacyElastics } from '../elastics/model';
import { ButtonSideSwatches } from '../elastics/ElasticsChart';
import { Odontogram } from '../elastics/Odontogram';
import {
    elasticSchemeName,
    elasticSchemeRangeLabel,
    schemesHaveElastics
} from '../elastics/persistence';
import { RecapEmpty, RecapRow } from './RecapPanel';

const NOOP = (): void => {};

function legacySide(elastic: string, size: string): string {
    if (!elastic) return '';
    return size ? `${elastic} (${size})` : elastic;
}

// Mirror the PDF: only schemes that actually carry elastics are shown.
function SchemeCards({ schemes }: { schemes: ElasticScheme[] }) {
    const populated = schemes.filter((s) => s.elastics.length > 0);
    return (
        <div className="flex flex-col gap-4">
            {populated.map((scheme) => (
                <div
                    key={scheme.id}
                    className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4"
                >
                    <div className="flex items-baseline justify-between gap-3">
                        <span className="text-sm font-semibold text-gray-800">
                            {elasticSchemeName(scheme)}
                        </span>
                        {elasticSchemeName(scheme) !== elasticSchemeRangeLabel(scheme) && (
                            <span className="text-xs font-medium text-gray-500">
                                {elasticSchemeRangeLabel(scheme)}
                            </span>
                        )}
                    </div>
                    <Odontogram placements={scheme.elastics} onChange={NOOP} readOnly />
                </div>
            ))}
        </div>
    );
}

// One divided list instead of a card per scheme; the chart key lives once in ElasticsLegend.
function CompactSchemes({ schemes }: { schemes: ElasticScheme[] }) {
    const populated = schemes.filter((s) => s.elastics.length > 0);
    return (
        <div className="divide-y divide-gray-100">
            {populated.map((scheme) => (
                <div key={scheme.id} className="flex flex-col gap-3 py-4 first:pt-1 last:pb-0">
                    <div className="flex items-baseline gap-2 text-sm">
                        <span className="font-medium text-gray-800">
                            {elasticSchemeRangeLabel(scheme)}
                        </span>
                        {elasticSchemeName(scheme) !== elasticSchemeRangeLabel(scheme) && (
                            <span className="text-gray-500">{elasticSchemeName(scheme)}</span>
                        )}
                    </div>
                    <Odontogram placements={scheme.elastics} onChange={NOOP} readOnly compact />
                </div>
            ))}
        </div>
    );
}

/** One-line chart key for a section of compact schemes. */
export function ElasticsLegend() {
    return (
        <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1 text-[11px] text-gray-500">
            <span>Numbered by install order · arrow = direction · Button, hook, cut-outs on:</span>
            <ButtonSideSwatches />
        </div>
    );
}

interface ElasticsRecapProps {
    schemes: ElasticScheme[];
    legacy?: LegacyElastics;
    /** Divided schemes with a capped chart beside its list; pair with ElasticsLegend on the section. */
    compact?: boolean;
}

/** Read-only elastics of an SDI/RDI: odontogram schemes, else the legacy Left/Right strings. */
export function ElasticsRecap({ schemes, legacy, compact = false }: ElasticsRecapProps) {
    if (schemesHaveElastics(schemes)) {
        return compact ? <CompactSchemes schemes={schemes} /> : <SchemeCards schemes={schemes} />;
    }
    if (!legacy) return <RecapEmpty>No elastics placed</RecapEmpty>;
    return (
        <>
            <RecapRow label="Left" value={legacySide(legacy.leftElastic, legacy.leftElasticSize)} />
            <RecapRow
                label="Right"
                value={legacySide(legacy.rightElastic, legacy.rightElasticSize)}
            />
        </>
    );
}
